'use client'

import { requestAdminNavigation } from '@/lib/admin-navigation'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  clearAdminSecret,
  getAdminExpiry,
  getAdminSecret,
  isAdminAuthStorageKey,
  setAdminSecret,
  subscribeAdminAuth,
  verifyAdminSecret,
} from '@/lib/admin-auth'
import { useQueryClient } from '@tanstack/react-query'
import { Eye, EyeOff, Lock } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

export function AdminAccessGate({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient()
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null)
  const [adminEmail, setAdminEmail] = useState('')
  const [inputSecret, setInputSecret] = useState('')
  const [showSecret, setShowSecret] = useState(false)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [authRevision, setAuthRevision] = useState(0)
  const verifiedLogin = useRef<{ secret: string, email: string } | null>(null)
  const authenticatedSecret = useRef<string | null>(null)

  useEffect(() => {
    const onAuthChanged = () => {
      authenticatedSecret.current = null
      queryClient.clear()
      setIsAuthenticated(null)
      setAdminEmail('')
      setAuthRevision(revision => revision + 1)
    }
    const onStorage = (event: StorageEvent) => {
      if (isAdminAuthStorageKey(event.key)) {
        verifiedLogin.current = null
        onAuthChanged()
      }
    }
    const onFocus = () => {
      if (getAdminSecret())
        setAuthRevision(revision => revision + 1)
    }

    const unsubscribe = subscribeAdminAuth(onAuthChanged)
    window.addEventListener('storage', onStorage)
    window.addEventListener('focus', onFocus)

    return () => {
      unsubscribe()
      window.removeEventListener('storage', onStorage)
      window.removeEventListener('focus', onFocus)
    }
  }, [queryClient])

  useEffect(() => {
    let cancelled = false
    const stored = getAdminSecret()
    if (!stored) {
      authenticatedSecret.current = null
      setIsAuthenticated(false)
      setAdminEmail('')
      return
    }

    const expires = getAdminExpiry()
    const expiryTimer = expires === null
      ? undefined
      : window.setTimeout(() => { getAdminSecret() }, Math.max(0, expires - Date.now() + 1))

    if (verifiedLogin.current?.secret === stored) {
      authenticatedSecret.current = stored
      setAdminEmail(verifiedLogin.current.email)
      setIsAuthenticated(true)
      verifiedLogin.current = null
      return () => {
        if (expiryTimer !== undefined)
          window.clearTimeout(expiryTimer)
      }
    }

    const wasAuthenticated = authenticatedSecret.current === stored
    if (!wasAuthenticated)
      setIsAuthenticated(null)
    verifyAdminSecret(stored).then((result) => {
      if (cancelled || getAdminSecret() !== stored)
        return
      if (result.ok) {
        authenticatedSecret.current = stored
        setAdminEmail(result.email)
        setIsAuthenticated(true)
      }
      else {
        setError(result.error)
        if (result.rejected)
          clearAdminSecret()
        if (result.rejected || !wasAuthenticated)
          setIsAuthenticated(false)
      }
    })

    return () => {
      cancelled = true
      if (expiryTimer !== undefined)
        window.clearTimeout(expiryTimer)
    }
  }, [authRevision])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    const secret = inputSecret.trim()
    const result = await verifyAdminSecret(secret)
    if (result.ok) {
      verifiedLogin.current = { secret, email: result.email }
      setAdminSecret(secret)
      setInputSecret('')
    }
    else {
      setError(result.error)
    }

    setIsLoading(false)
  }

  const handleLogout = () => {
    if (!requestAdminNavigation())
      return
    verifiedLogin.current = null
    clearAdminSecret()
    setInputSecret('')
    setError('')
  }

  if (isAuthenticated === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="size-6 animate-spin rounded-full border-2 border-current border-t-transparent" />
      </div>
    )
  }

  if (isAuthenticated) {
    return (
      <div className="relative">
        { }
        <div className="fixed right-4 top-4 z-50">
          <Button
            variant="outline"
            size="sm"
            onClick={handleLogout}
            className="bg-background/80 backdrop-blur-sm"
            title={adminEmail}
          >
            <Lock className="mr-2 size-4" />
            Logout
          </Button>
        </div>
        {children}
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-primary/10">
            <Lock className="size-6 text-primary" />
          </div>
          <CardTitle className="text-2xl">Admin Access</CardTitle>
          <p className="text-muted-foreground">
            Enter your Mentiss API secret (sk_mentiss_...). Your account must
            be a verified @mentiss.ai admin.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="api-secret">API Secret</Label>
              <div className="relative">
                <Input
                  id="api-secret"
                  type={showSecret ? 'text' : 'password'}
                  value={inputSecret}
                  onChange={e => setInputSecret(e.target.value)}
                  placeholder="sk_mentiss_..."
                  className="pr-10"
                  autoComplete="off"
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowSecret(!showSecret)}
                >
                  {showSecret ? (
                    <EyeOff className="size-4" />
                  ) : (
                    <Eye className="size-4" />
                  )}
                </Button>
              </div>
            </div>

            {error && (
              <div className="text-center text-sm text-red-500">{error}</div>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={isLoading || !inputSecret.trim()}
            >
              {isLoading ? (
                <>
                  <div className="mr-2 size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Verifying...
                </>
              ) : (
                <>
                  <Lock className="mr-2 size-4" />
                  Access Admin Panel
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
